import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Cookieless, PII-free click-heatmap capture. Records where visitors click on
// each page as percentages of the full page dimensions and posts aggregate
// batches to our own API (`POST /heatmap/collect`). Zero UX impact: the listener
// is passive, never preventDefault/stopPropagation, and all work is wrapped in
// try/catch so it can never break the site.

const API_BASE = process.env.REACT_APP_API || '';

const MAX_BUFFER = 200; // hard cap so a rapid clicker can't grow memory unbounded
const FLUSH_AT = 10; // send once we've collected roughly this many points
const FLUSH_INTERVAL_MS = 15000;

const clamp = (n) => Math.max(0, Math.min(100, n));

function HeatmapTracker() {
  const location = useLocation();
  const bufferRef = useRef([]);
  // Keep the current path in a ref so listeners/interval always flush against
  // the page the points were captured on, not a stale closure value.
  const pathRef = useRef(location.pathname);

  const flush = () => {
    try {
      if (!API_BASE) {
        bufferRef.current = [];
        return;
      }
      const points = bufferRef.current;
      if (!points.length) return;
      bufferRef.current = [];
      const body = JSON.stringify({
        path: pathRef.current,
        vw: window.innerWidth,
        points,
      });
      fetch(`${API_BASE}/heatmap/collect`, {
        method: 'POST',
        keepalive: true,
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body,
      }).catch(() => {});
    } catch (e) {
      /* heatmap capture must never break the site */
    }
  };

  // Flush on route change (attributing buffered points to the previous page),
  // then adopt the new path for subsequent clicks.
  useEffect(() => {
    flush();
    pathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      try {
        const docEl = document.documentElement;
        const pageWidth = docEl.scrollWidth;
        const pageHeight = docEl.scrollHeight;
        if (!pageWidth || !pageHeight) return;
        const x = clamp((e.pageX / pageWidth) * 100);
        const y = clamp((e.pageY / pageHeight) * 100);
        if (bufferRef.current.length >= MAX_BUFFER) return;
        bufferRef.current.push({ x, y });
        if (bufferRef.current.length >= FLUSH_AT) flush();
      } catch (err) {
        /* ignore */
      }
    };

    const onPageHide = () => flush();

    document.addEventListener('click', onClick, { passive: true });
    window.addEventListener('pagehide', onPageHide);
    const interval = setInterval(flush, FLUSH_INTERVAL_MS);

    return () => {
      document.removeEventListener('click', onClick, { passive: true });
      window.removeEventListener('pagehide', onPageHide);
      clearInterval(interval);
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export default HeatmapTracker;
